import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, Save, X } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../../common/apiClient";
import SummaryApi from "../../../common/SummaryApi";

const fmt = (n) =>
  n !== undefined && n !== null ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

const emptyGrade = () => ({
  gradeName: "",
  gradeCode: "",
  description: "",
  ctc: "",
  basic: "",
  hra: "",
  specialAllowance: "",
  lta: "",
  otherAllowances: "",
  bonus: "",
  pfDeduction: "",
  professionalTax: "",
  esiDeduction: "",
  tds: "",
  otherDeductions: "",
  isActive: true,
});

const Field = ({ label, required, error, hint, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
  </div>
);

const RupeeInput = ({ value, onChange, placeholder }) => (
  <div className="relative">
    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">₹</span>
    <input
      type="number"
      min={0}
      value={value}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      placeholder={placeholder}
      className="w-full border border-gray-200 rounded-lg pl-6 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
    />
  </div>
);

// ════════════════════════════════════════════════════════════════
const SalaryGradesPage = () => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyGrade());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);

  // Live preview in form
  const grossMonthly = Math.round(
    [form.basic, form.hra, form.specialAllowance, form.lta, form.otherAllowances, form.bonus]
      .reduce((s, v) => s + (Number(v) || 0), 0)
  );
  const netMonthly = Math.round(
    grossMonthly -
    [form.pfDeduction, form.professionalTax, form.esiDeduction, form.tds, form.otherDeductions]
      .reduce((s, v) => s + (Number(v) || 0), 0)
  );

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get(SummaryApi.getAllPayrollGrades.url, { params: { search } });
      setGrades(res.data.data || []);
    } catch {
      toast.error("Failed to load salary grades");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const openCreate = () => {
    setForm(emptyGrade());
    setEditingId(null);
    setErrors({});
    setShowForm(true);
  };

  const openEdit = (grade) => {
    setForm({
      gradeName: grade.gradeName || "",
      gradeCode: grade.gradeCode || "",
      description: grade.description || "",
      ctc: grade.ctc ?? "",
      basic: grade.basic ?? "",
      hra: grade.hra ?? "",
      specialAllowance: grade.specialAllowance ?? "",
      lta: grade.lta ?? "",
      otherAllowances: grade.otherAllowances ?? "",
      bonus: grade.bonus ?? "",
      pfDeduction: grade.pfDeduction ?? "",
      professionalTax: grade.professionalTax ?? "",
      esiDeduction: grade.esiDeduction ?? "",
      tds: grade.tds ?? "",
      otherDeductions: grade.otherDeductions ?? "",
      isActive: grade.isActive !== false,
    });
    setEditingId(grade._id);
    setErrors({});
    setShowForm(true);
  };

  const validate = () => {
    const errs = {};
    if (!form.gradeName.trim()) errs.gradeName = "Required";
    if (!form.ctc || Number(form.ctc) <= 0) errs.ctc = "Required";
    if (!form.basic || Number(form.basic) <= 0) errs.basic = "Required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editingId) {
        const res = await api.put(SummaryApi.updatePayrollGrade.url(editingId), form);
        setGrades((prev) => prev.map((g) => (g._id === editingId ? res.data.data : g)));
        toast.success("Grade updated");
      } else {
        const res = await api.post(SummaryApi.createPayrollGrade.url, form);
        setGrades((prev) => [...prev, res.data.data]);
        toast.success("Grade created");
      }
      setShowForm(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete grade "${name}"?`)) return;
    setDeleting(id);
    try {
      await api.delete(SummaryApi.deletePayrollGrade.url(id));
      setGrades((prev) => prev.filter((g) => g._id !== id));
      toast.success("Grade deleted");
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Salary Grades</h1>
          <p className="text-sm text-gray-500">
            Create pay band templates. Assign to employees when setting up salary — values auto-fill but can be edited.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90"
        >
          <Plus className="w-4 h-4" /> New Grade
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search grades..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
      />

      {/* Grade list */}
      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">Loading...</div>
      ) : grades.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          No salary grades yet. Create one to get started.
        </div>
      ) : (
        <div className="space-y-2">
          {grades.map((grade) => (
            <div key={grade._id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {/* Grade header row */}
              <div className="flex items-center justify-between px-4 py-3">
                <div
                  className="flex items-center gap-3 flex-1 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === grade._id ? null : grade._id)}
                >
                  {grade.gradeCode && (
                    <span className="text-xs font-bold px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                      {grade.gradeCode}
                    </span>
                  )}
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{grade.gradeName}</p>
                    {grade.description && (
                      <p className="text-xs text-gray-400 truncate max-w-xs">{grade.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="hidden sm:flex gap-4 text-right">
                    <div>
                      <p className="text-xs text-gray-400">CTC/yr</p>
                      <p className="text-sm font-semibold text-gray-800">{fmt(grade.ctc)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Net/mo</p>
                      <p className="text-sm font-semibold text-green-700">{fmt(grade.netMonthly)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEdit(grade)}
                      className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(grade._id, grade.gradeName)}
                      disabled={deleting === grade._id}
                      className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setExpandedId(expandedId === grade._id ? null : grade._id)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg"
                    >
                      {expandedId === grade._id
                        ? <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                        : <ChevronDown className="w-3.5 h-3.5 text-gray-500" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded breakdown */}
              {expandedId === grade._id && (
                <div className="border-t border-gray-100 px-4 pb-4 pt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 bg-gray-50/60">
                  {[
                    ["Annual CTC", grade.ctc],
                    ["Basic", grade.basic],
                    ["HRA", grade.hra],
                    ["Special Allowance", grade.specialAllowance],
                    ["LTA", grade.lta],
                    ["Bonus", grade.bonus],
                    ["Other Allowances", grade.otherAllowances],
                    ["PF Deduction", grade.pfDeduction],
                    ["Professional Tax", grade.professionalTax],
                    ["ESI Deduction", grade.esiDeduction],
                    ["TDS", grade.tds],
                    ["Other Deductions", grade.otherDeductions],
                    ["Gross Monthly", grade.grossMonthly],
                    ["Net Monthly", grade.netMonthly],
                  ].map(([label, val]) => (
                    <div key={label} className="bg-white rounded-lg p-2.5 border border-gray-100">
                      <p className="text-xs text-gray-400">{label}</p>
                      <p className="text-sm font-medium text-gray-800">{fmt(val)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Slide-over form ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/30" onClick={() => setShowForm(false)}>
          <div
            className="absolute top-0 right-0 w-full max-w-xl h-full bg-white shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Form header */}
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="font-bold text-gray-900">{editingId ? "Edit Grade" : "New Salary Grade"}</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Basic info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Grade Name" required error={errors.gradeName}>
                  <input
                    type="text"
                    value={form.gradeName}
                    onChange={(e) => set("gradeName", e.target.value)}
                    placeholder="e.g. L2 - Senior Engineer"
                    className={`w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.gradeName ? "border-red-400" : "border-gray-200"}`}
                  />
                </Field>
                <Field label="Grade Code" hint="Short identifier, e.g. L2">
                  <input
                    type="text"
                    value={form.gradeCode}
                    onChange={(e) => set("gradeCode", e.target.value.toUpperCase())}
                    placeholder="e.g. L2"
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Description">
                    <input
                      type="text"
                      value={form.description}
                      onChange={(e) => set("description", e.target.value)}
                      placeholder="Optional description"
                      className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </Field>
                </div>
              </div>

              {/* CTC */}
              <div>
                <Field label="Annual CTC" required error={errors.ctc}>
                  <RupeeInput value={form.ctc} onChange={(v) => set("ctc", v)} placeholder="Annual cost to company" />
                </Field>
              </div>

              {/* Monthly earnings */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Monthly Earnings</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Field label="Basic" required error={errors.basic}>
                    <RupeeInput value={form.basic} onChange={(v) => set("basic", v)} placeholder="Monthly" />
                  </Field>
                  <Field label="HRA">
                    <RupeeInput value={form.hra} onChange={(v) => set("hra", v)} placeholder="Monthly" />
                  </Field>
                  <Field label="Special Allowance">
                    <RupeeInput value={form.specialAllowance} onChange={(v) => set("specialAllowance", v)} placeholder="Monthly" />
                  </Field>
                  <Field label="LTA">
                    <RupeeInput value={form.lta} onChange={(v) => set("lta", v)} placeholder="Monthly" />
                  </Field>
                  <Field label="Bonus Component">
                    <RupeeInput value={form.bonus} onChange={(v) => set("bonus", v)} placeholder="Monthly" />
                  </Field>
                  <Field label="Other Allowances">
                    <RupeeInput value={form.otherAllowances} onChange={(v) => set("otherAllowances", v)} placeholder="Monthly" />
                  </Field>
                </div>
              </div>

              {/* Monthly deductions */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Monthly Deductions</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Field label="PF (Employee)" hint="12% of basic">
                    <RupeeInput value={form.pfDeduction} onChange={(v) => set("pfDeduction", v)} placeholder="Monthly" />
                  </Field>
                  <Field label="Professional Tax">
                    <RupeeInput value={form.professionalTax} onChange={(v) => set("professionalTax", v)} placeholder="Monthly" />
                  </Field>
                  <Field label="ESI">
                    <RupeeInput value={form.esiDeduction} onChange={(v) => set("esiDeduction", v)} placeholder="Monthly" />
                  </Field>
                  <Field label="TDS">
                    <RupeeInput value={form.tds} onChange={(v) => set("tds", v)} placeholder="Monthly" />
                  </Field>
                  <Field label="Other Deductions">
                    <RupeeInput value={form.otherDeductions} onChange={(v) => set("otherDeductions", v)} placeholder="Monthly" />
                  </Field>
                </div>
              </div>

              {/* Live preview */}
              {(grossMonthly > 0 || netMonthly > 0) && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 grid grid-cols-3 gap-3 text-center">
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs text-gray-400">Gross/mo</p>
                    <p className="text-sm font-bold">{fmt(grossMonthly)}</p>
                  </div>
                  <div className={`rounded-lg p-2 ${netMonthly > 0 ? "bg-green-600 text-white" : "bg-white"}`}>
                    <p className={`text-xs ${netMonthly > 0 ? "text-green-100" : "text-gray-400"}`}>Net/mo</p>
                    <p className="text-sm font-bold">{fmt(netMonthly)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs text-gray-400">Annual CTC</p>
                    <p className="text-sm font-bold">{form.ctc ? fmt(Number(form.ctc)) : "—"}</p>
                  </div>
                </div>
              )}

              {/* Active toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={form.isActive}
                  onChange={(e) => set("isActive", e.target.checked)}
                  className="w-4 h-4 accent-blue-600"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">Active (visible when assigning salary)</label>
              </div>
            </div>

            {/* Form footer */}
            <div className="flex gap-3 px-5 py-4 border-t">
              <button onClick={() => setShowForm(false)} className="flex-1 border rounded-lg py-2.5 text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving..." : editingId ? "Update Grade" : "Create Grade"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryGradesPage;